<template>
  <div class="editable-list-container grid gap-5">
    <!-- List Items -->
    <div class="grid gap-5">
      <div
        v-for="(item) in localItems"
        :key="item.id"
        class="bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 grid grid-cols-5 items-center gap-10 p-4"
      >
        <!-- Select Custom Field -->
        <div class="col-span-2">
          <BaseSelectWithLoadMore
            :name="'CustomFields' + item.id"
            v-model="item.id"
            value-key="value"
            label-key="label"
            placeholder="Select Custom Field"
            :options="allCustomFields"
            @input="onFieldSelect(item, $event)"
            required
            v-if="editingItem === item.id && item.isNew"
          />
          <div
            v-else
            class="w-full px-3 py-2 bg-white border border-gray-300 rounded-md min-h-[40px] flex items-center"
          >
            {{ getFieldLabel(item.id) || "Select Custom Field" }}
          </div>
        </div>

        <!-- Custom Field Value -->
        <div class="col-span-2">
          <input
            v-if="editingItem === item.id"
            v-model="item.value"
            type="text"
            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Enter Value"
          />
          <div
            v-else
            class="w-full px-3 py-2 bg-white border border-gray-300 rounded-md min-h-[40px] flex items-center"
          >
            {{ item.value || "Enter Value" }}
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center space-x-2">
          <button
            class="rounded-full transition-colors flex items-center justify-center w-11 h-11 shadow-md mx-1 border-none"
            :title="editingItem === item.id ? 'Save' : 'Edit'"
            :class="[editingItem === item.id ? 'text-green-600 hover:text-white bg-green-50 hover:bg-green-600' : 'text-blue-600 hover:text-white bg-blue-50 hover:bg-blue-600']"
            @click="toggleEdit(item)">
            <i
              class=" text-lg"
              :class="{
                'far fa-edit': editingItem !== item.id,
                'fas fa-check': editingItem === item.id
              }"></i>
          </button>
          <button
            class="text-red-600 hover:text-white bg-red-50 hover:bg-red-600 rounded-full transition-colors flex items-center justify-center w-11 h-11 shadow-md mx-1 border-none"
            title="Delete"
            @click="deleteItem(item)">
            <i class="fas fa-trash-alt text-lg"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Add New Item Button -->
    <V2BaseButton
      class="bg-blue-600 hover:bg-blue-700 px-5 py-3 text-white rounded-lg transition-colors font-medium w-full"
      @click="addNewItem">
      Add Custom Field
    </V2BaseButton>
  </div>
</template>

<script>
import BaseSelectWithLoadMore from '@/components/V2/BaseSelectWithLoadMore/BaseSelectWithLoadMore.vue';
import V2BaseButton from '@/components/V2/V2BaseButton.vue';
import { notificationAlerts } from '@/mixins';
import { deleteCustomFieldForEntity, getAllCustomFields, updateCustomFieldForEntity } from '@/services/V2/core/custom-fields.service';

export default {
  name: 'EntityCustomFields',
  components: {
    V2BaseButton,
    BaseSelectWithLoadMore,
  },

  mixins: [notificationAlerts],

  props: {
    items: {
      type: Array,
      default: () => [],
      required: true,
    },
    entity: {
      type: String,
      required: true,
    },
    entityId: {
      type: String,
      required: true,
    },
  },

  data () {
    return {
      localItems: [],
      editingItem: null,
      nextId: 1,
      allCustomFields: [],
    };
  },

  mounted () {
    this.getAllCustomFields();
  },

  watch: {
    items: {
      immediate: true,
      handler (newItems) {
        this.localItems = newItems.map(({ id, field, value }) => ({
          id,
          field,
          value,
          isNew: false, // Track if item is newly created
        }));
        this.nextId =
          Math.max(...this.localItems.map((item) => item.id), 0) + 1;
      },
    },
  },

  methods: {
    async getAllCustomFields () {
      try {
        const response = await getAllCustomFields();
        this.allCustomFields = response?.data?.map((item) => ({
          value: item?.id,
          label: item?.attributes?.label,
        }));
      } catch (e) {}
    },
    generateId () {
      return this.nextId++;
    },

    toggleEdit (item) {
      if (!item?.field || !item?.value) return;
      if (+this.editingItem === +item?.id) {
        this.onEdit(item);
        this.editingItem = null;
      } else {
        // Start editing
        this.editingItem = +item?.id;
      }
    },

    async onEdit (item) {
      const data = {
        entity: this.entity,
        'entity-id': this.entityId,
        value: item?.value,
      };
      try {
        await updateCustomFieldForEntity(item?.id, data);
        this.refresh();
        this.successNotification('UPDATED', 'Custom Field Updated Successfully!');
      } catch (e) {
        this.systemErrorNotification('ERROR', 'ERROR Updating Custom Field.');
      }
    },

    onFieldSelect (item, fieldId) {
      item.id = +fieldId;
      // Update the field label for display
      item.field = this.getFieldLabel(fieldId);
      this.editingItem = +fieldId;
    },

    getFieldLabel (fieldId) {
      if (!fieldId) return '';

      // Try both string and number comparison
      let field = this.allCustomFields.find((f) => f.value === fieldId);

      // If not found, try converting to string
      if (!field) {
        field = this.allCustomFields.find((f) => String(f.value) === String(fieldId));
      }

      // If still not found, try converting to number
      if (!field) {
        field = this.allCustomFields.find((f) => Number(f.value) === Number(fieldId));
      }

      return field ? field.label : '';
    },

    async deleteItem (item) {
      if (item?.isNew) {
        this.localItems.pop();
        this.editingItem = null;
      } else if (confirm('Are you sure you want to delete this item?')) {
        const data = {
          entity: this.entity,
          'entity-id': this.entityId,
        };
        try {
          await deleteCustomFieldForEntity(item?.id, data);
          this.refresh();
          this.successNotification('DELETED', 'Custom Field Deleted Successfully!');
        } catch (e) {
          this.systemErrorNotification('ERROR', 'ERROR Deleting Custom Field.');
        }
      }
    },

    addNewItem () {
      const newItem = {
        id: this.generateId(), // Generate a temporary ID for tracking
        field: '',
        value: '',
        isNew: true, // Mark as newly created
      };
      this.localItems.push(newItem);
      this.editingItem = newItem.id;
    },

    refresh () {
      this.$emit('onRefresh');
    },
  },
};
</script>
